import React from "react";
import { Instagram, Mail, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { BrainGlyph } from "./BrainGlyph";
import { useOffFeed } from "@/context/OffFeedContext";
import { ARGENTARIO_COORDS } from "./EditorialLabel";


export default function Footer() {
    const { t, lang } = useLang();
    const { openOffFeed } = useOffFeed();
    const en = lang === "en";

    return (
        <footer data-testid="main-footer" className="relative border-t border-white/10 bg-abnormal-ink text-abnormal-paperDim overflow-hidden">
            {/* Signature */}
            <div data-testid="footer-signature" className="border-b border-white/10 topo-lines">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-6 flex items-center justify-between label-mono text-abnormal-paperDim/60">
                    <span>ISSUE N° 26 · MMXXVI</span>
                    <span className="text-abnormal-acid">{ARGENTARIO_COORDS}</span>
                </div>
                <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-8 md:py-14">
                    <h2 className="font-display uppercase leading-[0.85] tracking-tighter text-abnormal-paper text-[56px] sm:text-[90px] md:text-[130px] lg:text-[160px]">
                        STAY ABNORMAL<span className="text-abnormal-acid">.</span>
                    </h2>
                    <p className="mt-4 label-mono text-abnormal-paperDim/50">
                        {en ? "// ideas travel further than trails" : "// le idee vanno più lontano dei sentieri"}
                    </p>
                </div>
            </div>

            {/* Ecosystem — two worlds, one universe */}
            <div data-testid="footer-ecosystem" className="border-b border-white/10">
                <div className="max-w-[1400px] mx-auto grid md:grid-cols-2">
                    <Link to="/" data-testid="footer-eco-it"
                        className="group block px-5 md:px-10 py-10 md:py-12 border-b md:border-b-0 md:border-r border-white/10 hover:bg-white/[0.02] transition-colors">
                        <p className="label-mono text-abnormal-acid">01 · {en ? "You are here" : "Sei qui"}</p>
                        <p className="mt-3 font-display uppercase text-4xl md:text-5xl leading-none text-abnormal-paper">ABNORMALWORLD<span className="text-abnormal-acid">.IT</span></p>
                        <p className="mt-2 font-display uppercase text-lg md:text-xl text-abnormal-paperDim tracking-tight">Ride · Trails · Argentario</p>
                        <p className="mt-3 text-sm max-w-sm leading-relaxed text-abnormal-paperDim/80">
                            {en ? "Enduro MTB on Monte Argentario. Live trail report, rides, events, community, where to stay."
                                : "Enduro MTB sul Monte Argentario. Trail report live, ride, eventi, community, dove dormire."}
                        </p>
                    </Link>
                    <Link to="/abnormal" data-testid="footer-eco-brand"
                        className="group block px-5 md:px-10 py-10 md:py-12 hover:bg-white/[0.02] transition-colors halftone">
                        <p className="label-mono text-abnormal-acid flex items-center gap-2">02 · {en ? "The brand" : "Il brand"} <ArrowRight size={12} /></p>
                        <p className="mt-3 font-display uppercase text-4xl md:text-5xl leading-none text-abnormal-paper">ABNORMAL<span className="text-abnormal-acid">.</span></p>
                        <p className="mt-2 font-display uppercase text-lg md:text-xl text-abnormal-paperDim tracking-tight">Story · Murph · Shop</p>
                        <p className="mt-3 text-sm max-w-sm leading-relaxed text-abnormal-paperDim/80">
                            {en ? "Story, Murph and the official shop. Same world, volume up."
                                : "Story, Murph e lo shop ufficiale. Stesso mondo, volume alzato."}
                        </p>
                    </Link>
                </div>
            </div>

            {/* Links */}
            <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 grid sm:grid-cols-2 md:grid-cols-5 gap-10">
                <div className="md:col-span-2 space-y-4">
                    <Link to="/" className="group inline-flex items-center gap-2.5">
                        <BrainGlyph size={20} strokeWidth={1.7} className="text-abnormal-acid transition-transform duration-500 group-hover:rotate-[10deg]" />
                        <h3 className="font-display text-3xl text-abnormal-paper leading-none">ABNORMALWORLD</h3>
                    </Link>
                    <p className="text-sm max-w-sm leading-relaxed">{t("footer.tagline")}</p>
                    <div className="flex items-center gap-4 pt-2 flex-wrap">
                        <a href="https://www.instagram.com/abnormalworld.it/" target="_blank" rel="noreferrer" aria-label="Instagram Abnormalworld" className="hover:text-abnormal-acid transition-colors"><Instagram size={20} /></a>
                        <a href="https://www.instagram.com/tuscanyurbancup/" target="_blank" rel="noreferrer" aria-label="Instagram Tuscany Urban Cup" className="hover:text-abnormal-acid transition-colors label-mono border border-white/20 px-2 py-1">@tuscanyurbancup</a>
                        {/* YouTube icon hidden until an official channel URL is provided */}
                        <a href="mailto:info@abnormalworld.it" aria-label="Email" className="hover:text-abnormal-acid transition-colors"><Mail size={20} /></a>
                    </div>
                </div>
                <div>
                    <p className="label-mono text-abnormal-acid mb-4">RIDE</p>
                    <ul className="space-y-2 text-sm">
                        <li><Link to="/trail" className="hover:text-abnormal-paper">{t("nav.trailReport")}</Link></li>
                        <li><Link to="/video" className="hover:text-abnormal-paper">{t("nav.video")}</Link></li>
                        <li><Link to="/guide" className="hover:text-abnormal-paper">{t("nav.guide")}</Link></li>
                        <li><Link to="/ride-call" className="hover:text-abnormal-paper">{t("nav.rideCall")}</Link></li>
                        <li><Link to="/eventi" className="hover:text-abnormal-paper">{t("nav.eventi")}</Link></li>
                        <li><Link to="/community" className="hover:text-abnormal-paper">{t("nav.community")}</Link></li>
                        <li><Link to="/stay" className="hover:text-abnormal-paper">{t("nav.stay")}</Link></li>
                    </ul>
                </div>
                <div>
                    <p className="label-mono text-abnormal-acid mb-4">ABNORMAL</p>
                    <ul className="space-y-2 text-sm">
                        <li><Link to="/abnormal" className="hover:text-abnormal-paper">{t("nav.abnormal")}</Link></li>
                        <li><Link to="/abnormal#story" className="hover:text-abnormal-paper">{t("nav.story")}</Link></li>
                        <li><Link to="/abnormal#murph" className="hover:text-abnormal-paper">{t("nav.murph")}</Link></li>
                        <li><Link to="/shop" className="hover:text-abnormal-paper">{t("nav.shop")}</Link></li>
                        <li><Link to="/arcade" className="hover:text-abnormal-paper">{t("nav.arcade")}</Link></li>
                    </ul>
                </div>
                <div>
                    <p className="label-mono text-abnormal-acid mb-4">{t("footer.info")}</p>
                    <ul className="space-y-2 text-sm">
                        <li>Monte Argentario, GR</li>
                        <li>Toscana, Italia</li>
                        <li><a href="mailto:info@abnormalworld.it" className="hover:text-abnormal-paper">info@abnormalworld.it</a></li>
                        <li className="pt-2"><Link to="/privacy" data-testid="footer-privacy" className="hover:text-abnormal-paper">{t("footer.privacy")}</Link></li>
                        <li><Link to="/cookie-policy" data-testid="footer-cookie" className="hover:text-abnormal-paper">{t("footer.cookie")}</Link></li>
                        <li><Link to="/shop/info?tab=shipping" data-testid="footer-shipping" className="hover:text-abnormal-paper">{t("footer.shipping")}</Link></li>
                        <li><Link to="/shop/info?tab=refund" data-testid="footer-returns" className="hover:text-abnormal-paper">{t("footer.returns")}</Link></li>
                        <li><Link to="/shop/info?tab=terms" data-testid="footer-terms" className="hover:text-abnormal-paper">{t("footer.terms")}</Link></li>
                    </ul>
                </div>
            </div>

            <div className="border-t border-white/10 text-xs text-abnormal-paperDim/60 px-5 md:px-10 py-5 flex justify-between max-w-[1400px] mx-auto flex-wrap gap-2 items-center">
                <span>© {new Date().getFullYear()} Abnormal World · {t("footer.copyright")}</span>
                <div className="flex items-center gap-6">
                    <button type="button" onClick={(e) => openOffFeed(e.currentTarget)} data-testid="footer-offfeed-link" aria-label="Enter Off-Feed"
                        className="label-mono text-white/25 hover:text-abnormal-acid transition-colors">
                        {en ? "// enter off-feed" : "// entra in off-feed"}
                    </button>
                    <span className="label-mono">{t("footer.madein")}</span>
                </div>
            </div>
        </footer>
    );
}
